import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@ordo/ui/input-group'
import { Label } from '@ordo/ui/label'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@ordo/ui/sheet'
import { toast } from '@ordo/ui/sonner'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CreditCard } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

/** Sentinel for "collect a brand-new card in the Stripe Element". */
const NEW_CARD = '__new_card__'

import { topUpPresetsForMinimum } from '@ordo/constants/wallet'

import {
  type BillingPaymentMethod,
  createTestTopUp,
  createTopUpIntent,
  type CreateTopUpIntentVariables,
  type StripeIntentResult,
  type TestTopUpResult,
  useBillingConfigQuery,
} from '@/api/billing-family'

import { AmountPresetGrid } from './amount-preset-grid'
import { describeCard, formatCents, householdCoverageLine, resolveTopUpConfirmStep } from './helpers'
import { StripeElementsForm } from './stripe-elements-lazy'

/**
 * The account slice the sheet actually reads. `BillingFamilyAccount`
 * satisfies it structurally; other wallets (e.g. the school checkout's
 * ORGANIZATION account) can pass a lean adapter — the "Card on file"
 * block simply hides when `paymentMethods` is empty.
 */
export interface TopUpSheetAccount {
  id: string
  currency: string
  /**
   * The smallest load-up this account may make, in cents (PROD-1317) — the
   * ONLY amount rule the sheet enforces: anything at or above it is
   * chargeable, quick amounts are the presets that clear it, and the
   * server's validator applies the same floor. Family wallets take the
   * resolved per-household value off `BillingFamilyAccount`; a lean adapter
   * for a non-family wallet must supply one.
   */
  minTopUpCents: number
  paymentMethods: BillingPaymentMethod[]
  defaultPaymentMethodRef: string | null
  /**
   * Names the wallet this sheet charges, so the target is never implicit
   * (PROD-1313). `displayName` alone is NOT enough to identify it — the name
   * derives from adult surnames, so a guardian on two households is served
   * the same one twice — hence the coverage fields beside it.
   *
   * All three optional: a non-family surface (school checkout) keeps its lean
   * adapter and falls back to the unnamed copy, while `BillingFamilyAccount`
   * satisfies them structurally with no call-site change.
   */
  displayName?: string | null
  studentNames?: readonly string[]
  schoolNames?: readonly string[]
}

/**
 * Endpoint override — lets a non-family surface reuse the sheet against
 * its own top-up routes (school checkout → `/api/billing-school/account/*`).
 * Omitted = the family wallet endpoints, with behavior identical to before
 * this prop existed.
 */
export interface TopUpSheetApi {
  createTopUpIntent: (vars: CreateTopUpIntentVariables) => Promise<StripeIntentResult>
  createTestTopUp: (vars: CreateTopUpIntentVariables) => Promise<TestTopUpResult>
  /**
   * Query key invalidated after a LOCAL TEST top-up (which credits
   * synchronously and never fires `onPaid`), so the caller's balance
   * read refreshes without its own wiring.
   */
  testTopUpInvalidateKey: readonly unknown[]
}

export interface TopUpSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  account: TopUpSheetAccount
  /**
   * Fired after a Stripe payment is confirmed. The credit lands via the
   * `payment_intent.succeeded` webhook a beat later, so the page starts
   * watching the balance until it appears rather than trusting a single
   * refetch. (Test top-ups credit synchronously and don't call this.)
   */
  onPaid: (args: { accountId: string; amountCents: number }) => void
  /** Override the top-up endpoints; default = the family wallet's. */
  api?: TopUpSheetApi
  /**
   * Prefill applied each time the sheet OPENS: a value that is one of the
   * account's OFFERED quick amounts selects that button; anything else
   * lands in the custom-amount field. The user can still edit or pick a
   * quick amount. Callers derive it with `topUpPrefillCents`, which is
   * threaded the same `minTopUpCents` so a prefill is never an amount this
   * sheet would then refuse.
   */
  initialAmountCents?: number | null
  /** Muted helper line under the amount input (e.g. "Minimum to place order is $X"). */
  amountHelperText?: string | null
  /**
   * Extra secondary badge on the "Card on file" block, e.g.
   * "Organization payment method" on school checkout — the saved card
   * there belongs to the org's shared account, and unlabeled it reads
   * like another admin's personal card leaked in.
   */
  paymentMethodBadge?: string | null
  /**
   * Load-up incentive rate in basis points (100 = 1%) when THIS account
   * earns a bonus wallet credit on settled top-ups (qualifying
   * ORGANIZATION/PREPAID accounts — see `creditTopUpIncentive` server
   * side). Renders a callout under the amount input with the live bonus
   * for the entered amount. Omit/0 = no callout (family wallets).
   */
  incentiveBps?: number | null
}

/** Cents → the custom-amount input's dollar string ("6400 → 64", "6337 → 63.37"). */
function centsToDollarInput(cents: number): string {
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2)
}

/** 100 bps → "1%", 250 bps → "2.5%" (trailing zeros trimmed). */
export function formatBpsPercent(bps: number): string {
  return `${(bps / 100).toFixed(2).replace(/\.?0+$/, '')}%`
}

export function TopUpSheet({
  open,
  onOpenChange,
  account,
  onPaid,
  api,
  initialAmountCents,
  amountHelperText,
  paymentMethodBadge,
  incentiveBps,
}: TopUpSheetProps) {
  // Name the wallet this sheet is about to charge. A multi-household
  // guardian's two households carry the SAME display name, so the coverage
  // line is what actually disambiguates them (PROD-1313).
  const householdName = account.displayName?.trim() || null
  const coverage = householdCoverageLine(account)
  /** Suffix for the copy that commits the charge. */
  const toHousehold = householdName ? ` to ${householdName}` : ''
  // Ordo-owned saved-card picker. `account.paymentMethods` is already filtered
  // server-side to the cards THIS viewer may charge, so anything listed here is
  // authorized (the server re-checks on submit regardless).
  const usableCards = account.paymentMethods
  const defaultCardId =
    usableCards.find((m) => m.isDefault)?.stripePaymentMethodId ?? usableCards[0]?.stripePaymentMethodId ?? NEW_CARD
  const [selectedCardId, setSelectedCardId] = useState<string>(defaultCardId)
  // The quick amounts this account may actually pick: the presets that clear
  // its minimum load-up. Never empty — a minimum past the largest preset
  // leaves the minimum itself as the only quick amount — so its first entry
  // is always a legal starting selection.
  const offeredPresets = useMemo(() => topUpPresetsForMinimum(account.minTopUpCents), [account.minTopUpCents])
  const [amountCents, setAmountCents] = useState<number>(() => offeredPresets[0])
  const [customAmount, setCustomAmount] = useState<string>('')
  // When set, the sheet is in the confirm step. `savedCardLabel` non-null
  // means a saved card is already attached to the intent server-side, so
  // there is nothing to collect — otherwise the Payment Element is mounted
  // against this PaymentIntent (plus the CustomerSession that renders the
  // opt-in save checkbox — never saved cards) and the browser confirms the
  // charge.
  const [paymentSession, setPaymentSession] = useState<{
    clientSecret: string
    customerSessionClientSecret: string | null
    savedCardLabel: string | null
  } | null>(null)

  // Prefill on every open transition (ref so a shortfall recompute while
  // the sheet is open never stomps what the user typed). An OFFERED quick
  // amount selects its button; anything else goes to the custom field —
  // which now accepts it, since the minimum is the only rule left.
  const initialAmountRef = useRef(initialAmountCents)
  initialAmountRef.current = initialAmountCents
  useEffect(() => {
    const prefill = initialAmountRef.current
    if (open && prefill != null && prefill > 0) {
      if (offeredPresets.includes(prefill)) {
        setAmountCents(prefill)
        setCustomAmount('')
      } else {
        setCustomAmount(centsToDollarInput(prefill))
      }
    }
  }, [open, offeredPresets])

  const config = useBillingConfigQuery()
  const stripeConfigured = Boolean(config.data?.stripePublishableKey)
  // Gate submission on the config query settling — while it's loading (or
  // errored), `stripeConfigured` reads false, and a fast submit would wrongly
  // route a production top-up to the non-prod test endpoint.
  const configReady = config.isSuccess
  const qc = useQueryClient()
  // Same mutations the family hooks build (`useCreateTopUpIntentMutation` /
  // `useCreateTestTopUpMutation`), inlined so the `api` prop can swap the
  // endpoint while defaults stay behavior-identical — including the test
  // top-up's `['billing', 'family']` invalidation.
  const createIntentMutation = useMutation({ mutationFn: api?.createTopUpIntent ?? createTopUpIntent })
  const testTopUpMutation = useMutation({
    mutationFn: api?.createTestTopUp ?? createTestTopUp,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: api ? [...api.testTopUpInvalidateKey] : ['billing', 'family'] })
    },
  })
  const loading = createIntentMutation.isPending || testTopUpMutation.isPending || !configReady

  const effectiveAmountCents = customAmount.trim().length > 0 ? Math.round(Number(customAmount) * 100) : amountCents
  const defaultPaymentMethod = pickDefaultPaymentMethod(account)

  const closeSheet = (nextOpen: boolean) => {
    if (!nextOpen) {
      setPaymentSession(null)
    }
    onOpenChange(nextOpen)
  }

  useEffect(() => {
    if (open) {
      setSelectedCardId(defaultCardId)
    }
  }, [open, defaultCardId])

  const handleTopUp = async () => {
    // The minimum load-up is the ONLY amount rule (PROD-1317): any amount at
    // or above the school's configured floor is chargeable, so a typed
    // in-between amount is no longer bounced back to a quick amount. It also
    // subsumes the old "greater than zero" check, since a configured minimum
    // can never be set below `MIN_TOP_UP_FLOOR_CENTS`.
    if (!Number.isFinite(effectiveAmountCents) || effectiveAmountCents < account.minTopUpCents) {
      toast.error(`Enter a load-up of ${formatCents(account.minTopUpCents, account.currency)} or more.`)
      return
    }
    // Local dev without Stripe keys: credit the wallet directly through the
    // non-production test endpoint so the flow stays testable end-to-end.
    if (!stripeConfigured) {
      try {
        await testTopUpMutation.mutateAsync({ amountCents: effectiveAmountCents, accountId: account.id })
        toast.success(`Added ${formatCents(effectiveAmountCents, account.currency)} to your balance (test).`)
        closeSheet(false)
      } catch (err) {
        toast.error('Could not add funds (test)', {
          description: err instanceof Error ? err.message : String(err),
        })
      }
      return
    }
    const chosenPaymentMethodId = selectedCardId === NEW_CARD ? null : selectedCardId
    try {
      const intent = await createIntentMutation.mutateAsync({
        amountCents: effectiveAmountCents,
        // Multi-household guardians have several wallets; the server 409s
        // rather than guessing, so always name the household this sheet is
        // rendered for.
        accountId: account.id,
        // Ordo picks the saved card, not Stripe's Element — the Element would
        // redisplay every card on the household's single Stripe customer,
        // including other guardians' private ones. NEW_CARD falls through to
        // the Element with no saved-card list.
        ...(chosenPaymentMethodId ? { paymentMethodId: chosenPaymentMethodId } : {}),
      })
      if (!intent.clientSecret) {
        toast.error('Could not start adding funds', { description: 'Stripe did not return a client secret.' })
        return
      }
      setPaymentSession({
        clientSecret: intent.clientSecret,
        ...resolveTopUpConfirmStep({
          chosenPaymentMethodId,
          cards: usableCards,
          customerSessionClientSecret: intent.customerSessionClientSecret,
        }),
      })
    } catch (err) {
      toast.error('Could not start adding funds', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  const handlePaymentSuccess = () => {
    setPaymentSession(null)
    toast.success(
      `Payment of ${formatCents(effectiveAmountCents, account.currency)} submitted. Your balance will update here as soon as it clears.`,
    )
    // The wallet is credited by the payment_intent.succeeded webhook, not this
    // request — hand off to the page's balance watcher, which polls until the
    // credit lands (showing an "Updating…" state) instead of racing it with a
    // single refetch.
    onPaid({ accountId: account.id, amountCents: effectiveAmountCents })
    onOpenChange(false)
  }

  return (
    <Sheet open={open} onOpenChange={closeSheet}>
      <SheetContent side="right" size="lg" className="flex flex-col gap-0 p-0">
        <SheetHeader className="shrink-0 border-b px-6 pb-4 pt-6">
          <SheetTitle>{householdName ? `Add funds to ${householdName}` : 'Add funds'}</SheetTitle>
          <SheetDescription>
            {coverage ? <span className="block font-medium text-foreground">{coverage}</span> : null}
            {householdName ? null : 'Add funds to your Ordo balance. '}
            Apple Pay and Google Pay are supported in addition to cards and bank accounts.
          </SheetDescription>
        </SheetHeader>

        {paymentSession ? (
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
            <p className="text-sm text-muted-foreground">
              Paying {formatCents(effectiveAmountCents, account.currency)}
              {toHousehold}. Your balance updates as soon as the payment is confirmed.
            </p>
            <StripeElementsForm
              clientSecret={paymentSession.clientSecret}
              customerSessionClientSecret={paymentSession.customerSessionClientSecret}
              mode="payment"
              confirmOnly={
                paymentSession.savedCardLabel ? (
                  <div className="flex items-center gap-3 rounded-md border bg-muted/20 px-3 py-3">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-background text-muted-foreground">
                      <CreditCard className="h-5 w-5" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{paymentSession.savedCardLabel}</p>
                      <p className="text-xs text-muted-foreground">
                        This card will be charged when you confirm — no need to re-enter it.
                      </p>
                    </div>
                  </div>
                ) : undefined
              }
              submitLabel={`Pay ${formatCents(effectiveAmountCents, account.currency)}${toHousehold}`}
              onSuccess={handlePaymentSuccess}
              onCancel={() => setPaymentSession(null)}
            />
          </div>
        ) : (
          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(e) => {
              e.preventDefault()
              void handleTopUp()
            }}>
            <div className="flex-1 space-y-6 overflow-y-auto px-6 py-4">
              {stripeConfigured && usableCards.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-medium text-foreground">Pay with</p>
                  <RadioGroup value={selectedCardId} onValueChange={setSelectedCardId} className="flex flex-col gap-2">
                    {usableCards.map((method) => (
                      <Label
                        key={method.stripePaymentMethodId}
                        htmlFor={`top-up-card-${method.stripePaymentMethodId}`}
                        className="flex cursor-pointer items-center gap-3 rounded-lg border border-border px-3 py-2.5 transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                        <RadioGroupItem
                          id={`top-up-card-${method.stripePaymentMethodId}`}
                          value={method.stripePaymentMethodId}
                        />
                        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{describeCard(method)}</span>
                        {method.addedByUserId !== null && !method.isMine ? (
                          <Badge variant="outline" className="shrink-0">
                            Shared with you
                          </Badge>
                        ) : null}
                      </Label>
                    ))}
                    <Label
                      htmlFor="top-up-card-new"
                      className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-border px-3 py-2.5 transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                      <RadioGroupItem id="top-up-card-new" value={NEW_CARD} />
                      <span className="text-sm text-foreground">Use a new card</span>
                    </Label>
                  </RadioGroup>
                </div>
              ) : null}
              <div className="flex flex-col gap-2">
                <Label>Quick amount</Label>
                <AmountPresetGrid
                  presetsCents={offeredPresets}
                  value={customAmount.trim().length === 0 ? amountCents : null}
                  onSelect={(cents) => {
                    setAmountCents(cents)
                    setCustomAmount('')
                  }}
                  currency={account.currency}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="topup-custom-amount">Or enter a custom amount</Label>
                <InputGroup>
                  <InputGroupAddon>$</InputGroupAddon>
                  <InputGroupInput
                    id="topup-custom-amount"
                    type="number"
                    min={account.minTopUpCents / 100}
                    step="0.01"
                    placeholder="0.00"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                  />
                </InputGroup>
                {/* Deliberately phrased around LOADING rather than as a bare
                    "Minimum …": on checkout the caller's `amountHelperText`
                    below reads "Minimum to place order is $X" (the order's
                    shortfall), and the two numbers are unrelated. */}
                <p className="text-xs text-muted-foreground">
                  You can load any amount of {formatCents(account.minTopUpCents, account.currency)} or more.
                </p>
                {amountHelperText ? <p className="text-xs text-muted-foreground">{amountHelperText}</p> : null}
              </div>

              {incentiveBps && incentiveBps > 0 ? (
                <div className="rounded-md border border-emerald-600/25 bg-emerald-50 px-3 py-2.5 dark:bg-emerald-900/20">
                  <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                    {formatBpsPercent(incentiveBps)} load-up bonus
                  </p>
                  <p className="mt-0.5 text-xs text-emerald-800/80 dark:text-emerald-200/80">
                    {Number.isFinite(effectiveAmountCents) && effectiveAmountCents > 0
                      ? `Add ${formatCents(effectiveAmountCents, account.currency)} and ${formatCents(
                          Math.round((effectiveAmountCents * incentiveBps) / 10000),
                          account.currency,
                        )} extra is credited when the payment settles — ${formatCents(
                          effectiveAmountCents + Math.round((effectiveAmountCents * incentiveBps) / 10000),
                          account.currency,
                        )} added to the balance in total.`
                      : `Adding funds earns an extra ${formatBpsPercent(incentiveBps)} credited to the balance.`}
                  </p>
                </div>
              ) : null}

              {stripeConfigured && defaultPaymentMethod ? (
                <div className="flex flex-col gap-2">
                  <Label>Card on file</Label>
                  <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-background text-muted-foreground">
                        <CreditCard className="h-5 w-5" aria-hidden />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-sm font-medium">
                            {formatPaymentMethodLabel(defaultPaymentMethod)}
                          </p>
                          {defaultPaymentMethod.isDefault ? <Badge variant="secondary">Default</Badge> : null}
                          {paymentMethodBadge ? <Badge variant="secondary">{paymentMethodBadge}</Badge> : null}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatPaymentMethodDetail(defaultPaymentMethod)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    You&apos;ll choose how to pay on the next step — saved cards, Apple Pay, and Google Pay included.
                  </p>
                </div>
              ) : null}
            </div>

            <div className="flex shrink-0 items-center justify-end gap-2 border-t px-6 py-4">
              <Button type="button" variant="outline" onClick={() => closeSheet(false)} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading
                  ? 'Starting...'
                  : stripeConfigured
                    ? `Continue to payment`
                    : `Add ${formatCents(effectiveAmountCents, account.currency)}${toHousehold}`}
              </Button>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  )
}

function pickDefaultPaymentMethod(account: TopUpSheetAccount): BillingPaymentMethod | null {
  return (
    account.paymentMethods.find(
      (method) =>
        method.id === account.defaultPaymentMethodRef ||
        method.stripePaymentMethodId === account.defaultPaymentMethodRef,
    ) ??
    account.paymentMethods.find((method) => method.isDefault) ??
    account.paymentMethods[0] ??
    null
  )
}

function formatPaymentMethodLabel(method: BillingPaymentMethod): string {
  if (method.walletType === 'apple_pay') {
    return method.last4 ? `Apple Pay ending ${method.last4}` : 'Apple Pay'
  }
  if (method.walletType === 'google_pay') {
    return method.last4 ? `Google Pay ending ${method.last4}` : 'Google Pay'
  }
  const brand = method.brand ? toTitleCase(method.brand) : toTitleCase(method.type || 'card')
  return method.last4 ? `${brand} ending ${method.last4}` : brand
}

function formatPaymentMethodDetail(method: BillingPaymentMethod): string {
  if (method.expMonth && method.expYear) {
    return `Expires ${String(method.expMonth).padStart(2, '0')}/${method.expYear}`
  }
  return toTitleCase(method.type || 'Saved payment method')
}

function toTitleCase(value: string): string {
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}
